/**
 * THE SYNC QUEUE: reading it, and the one call that approves or declines an
 * offer on it.
 *
 * Lifted out of `PartnerSync/index.jsx` unchanged when the auto-approval
 * drill-down needed the same two things. It is here rather than there because
 * the drill-down's "Approve anyway" and "Reject" buttons must be the SAME
 * action as the review grid's — one post, one body shape, one confirmation
 * sentence. A second copy of an approve call is a second place a partner can
 * be told the wrong thing.
 *
 * ── WHY THE DRILL-DOWN HAS TO READ THIS QUEUE AT ALL ──
 *
 * `StateHash` is required by the sync endpoint and IS NOT ON A DECISION
 * RECORD. It lives on the queue row's `Metadata.state_hash`, and it is what
 * the partner checks to see whether anything changed on their side since we
 * fetched the offer. So acting on an offer means finding its queue row first;
 * an offer no longer on the queue cannot be acted on from here at all, and the
 * drill-down disables its buttons and says so rather than posting an empty
 * hash and hoping.
 */

import { post } from '../../Services/API';
import fetchPartnerBookingsForSync from '../../Services/PartnerIntegrationService';

/** The flows the sync endpoint accepts. Not free text at the call sites. */
export const SYNC_FLOW = { APPROVED: 'APPROVED', DECLINED: 'DECLINED' };

export const DECLINE_CONFIRMATION = 'Are you sure you want to decline this booking? This action cannot be undone.';

/**
 * One queue row with its three JSON columns parsed.
 *
 * `ProfitAnalysis` is parsed defensively and nulled on failure — it is a cue
 * on a card, and a malformed one must not take the queue down with it. `Data`
 * and `Metadata` are not: without them there is no booking and no state hash,
 * so a row that cannot be read is a row that cannot be rendered.
 */
export const parseSyncRow = (booking) => {
  let profitAnalysis = null;
  if (booking.ProfitAnalysis) {
    try {
      profitAnalysis = JSON.parse(booking.ProfitAnalysis);
    } catch {
      profitAnalysis = null;
    }
  }

  return {
    ...booking,
    Data: JSON.parse(booking.Data),
    Metadata: JSON.parse(booking.Metadata),
    ProfitAnalysis: profitAnalysis,
  };
};

/** Every offer still on the queue, parsed. */
export const fetchSyncQueue = async () => {
  const response = await fetchPartnerBookingsForSync();
  const rows = response?.data?.data;
  return Array.isArray(rows) ? rows.map(parseSyncRow) : [];
};

/**
 * The queue row for one partner reference, or null.
 *
 * Matched on `BookingId`, which is the partner's own reference and the same
 * value a decision record calls `reference` — that equality is what lets the
 * drill-down act on an offer it only knows by reference.
 */
export const findQueueRow = (rows, reference) => (rows || [])
  .find((row) => String(row?.BookingId) === String(reference)) || null;

/**
 * Approve or decline. The ONLY place either is posted.
 *
 * Takes the parsed `Data` and the state hash rather than a queue row, because
 * the review grid has them to hand separately and the drill-down reads them
 * off the row it found — and because a function that takes exactly the four
 * values the endpoint wants cannot be called with the wrong half of a row.
 */
export const postSyncDecision = ({ order = {}, flow, stateHash }) => post(
  'partner-integrations/sync',
  {
    BookingId: order.partner_booking_reference,
    PartnerId: order.partner_id,
    flow,
    StateHash: stateHash,
  },
);
